import {
  ConfigurationStatus,
  ControlAction,
  FileParsingBatchStatus,
  FileProcessingStatus,
} from '../../domain/enums';
import {
  ActionsCatalog,
  AssessmentMatch,
  AssessmentsList,
  BatchesPage,
  BatchHeatmapCell,
  Configuration,
  ControlResult,
  FileRecordPreview,
  IngestionDetail,
  IngestionSummary,
  IngestionSummaryStats,
  ListBatchesParams,
  ListConfigurationsParams,
  ListIngestionsParams,
  PageInfo,
  PaginatedResult,
  ValidationReport,
} from '../../domain/types';
import {
  ListBatchesInput,
  ListConfigurationsInput,
  ListIngestionsInput,
  PdgActionsCatalog,
  PdgAssessmentMatch,
  PdgAssessmentsList,
  PdgBatchesList,
  PdgBatchHeatmapCell,
  PdgConfiguration,
  PdgConfigurationStatus,
  PdgControlAction,
  PdgControlResult,
  PdgFileParsingBatchStatus,
  PdgFileProcessingStatus,
  PdgFileRecordPreview,
  PdgIngestionDetail,
  PdgIngestionSummary,
  PdgIngestionSummaryStats,
  PdgPageInfo,
  PdgValidationReport,
} from './types';

/**
 * Maps between the generated `Pdg*` GraphQL types in `./types` and the
 * transport-agnostic domain types. Keeping the mapping in one place means
 * a future non-Apollo adapter only needs its own mapper, never a change to
 * the domain types or to any hook/component that consumes them.
 *
 * Three things this file exists to absorb:
 *
 * - **`__typename`.** Every mapper copies fields explicitly rather than
 *   spreading, so Apollo internals never leak into the domain layer.
 * - **Nullability.** Codegen emits nullable schema fields as optional
 *   (`field?: Maybe<T>`, i.e. `T | null | undefined`); the domain model uses
 *   a plain `T | null`, so each one is normalised with `?? null`.
 * - **Enums.** `domain/enums` and the generated `Pdg*` enums carry identical
 *   wire values, but TypeScript treats two enum declarations as distinct
 *   nominal types. The exhaustive `Record`s below convert between them, and
 *   are deliberately not casts: if the BFF adds an enum value, the next
 *   `generate-types` run turns it into a compile error here rather than a
 *   value that silently escapes into the domain layer.
 *
 * BIGINT counters (`totalRowsParsed`, byte offsets, source-resolution
 * counts) are `String` in the schema and stay strings throughout — no
 * mapper may coerce one to `number`. `JSONObject` payloads are opaque and
 * are forwarded by reference, never rebuilt.
 */

const domainFileProcessingStatus: Record<
  PdgFileProcessingStatus,
  FileProcessingStatus
> = {
  [PdgFileProcessingStatus.Pending]: FileProcessingStatus.Pending,
  [PdgFileProcessingStatus.Parsing]: FileProcessingStatus.Parsing,
  [PdgFileProcessingStatus.Validating]: FileProcessingStatus.Validating,
  [PdgFileProcessingStatus.Resolving]: FileProcessingStatus.Resolving,
  [PdgFileProcessingStatus.Finalizing]: FileProcessingStatus.Finalizing,
  [PdgFileProcessingStatus.Paused]: FileProcessingStatus.Paused,
  [PdgFileProcessingStatus.Cancelled]: FileProcessingStatus.Cancelled,
  [PdgFileProcessingStatus.Completed]: FileProcessingStatus.Completed,
  [PdgFileProcessingStatus.CompletedWithErrors]:
    FileProcessingStatus.CompletedWithErrors,
  [PdgFileProcessingStatus.Failed]: FileProcessingStatus.Failed,
  [PdgFileProcessingStatus.Rejected]: FileProcessingStatus.Rejected,
};

const pdgFileProcessingStatus: Record<
  FileProcessingStatus,
  PdgFileProcessingStatus
> = {
  [FileProcessingStatus.Pending]: PdgFileProcessingStatus.Pending,
  [FileProcessingStatus.Parsing]: PdgFileProcessingStatus.Parsing,
  [FileProcessingStatus.Validating]: PdgFileProcessingStatus.Validating,
  [FileProcessingStatus.Resolving]: PdgFileProcessingStatus.Resolving,
  [FileProcessingStatus.Finalizing]: PdgFileProcessingStatus.Finalizing,
  [FileProcessingStatus.Paused]: PdgFileProcessingStatus.Paused,
  [FileProcessingStatus.Cancelled]: PdgFileProcessingStatus.Cancelled,
  [FileProcessingStatus.Completed]: PdgFileProcessingStatus.Completed,
  [FileProcessingStatus.CompletedWithErrors]:
    PdgFileProcessingStatus.CompletedWithErrors,
  [FileProcessingStatus.Failed]: PdgFileProcessingStatus.Failed,
  [FileProcessingStatus.Rejected]: PdgFileProcessingStatus.Rejected,
};

/**
 * Status used when the wire carries a value this build does not know yet.
 * Non-terminal on purpose, so the detail page keeps polling and picks up the
 * terminal status when it lands.
 */
export const UNKNOWN_FILE_PROCESSING_STATUS_FALLBACK =
  FileProcessingStatus.Pending;

const reportedUnknownFileProcessingStatuses = new Set<string>();

/**
 * The exhaustive `Record` above catches new enum values at compile time, but
 * only after `generate-types` runs. A BFF deployed ahead of this app can
 * still send a value that is missing here (ROI-2301: `VALIDATING`,
 * `RESOLVING`, `FINALIZING`). Map it to a non-terminal fallback and
 * warn once instead of letting `undefined` reach the UI. `console.warn`
 * rather than Sentry: the module boundary keeps error-reporting SDKs out
 * (see `__tests__/module-boundary.spec.ts`).
 */
export const toDomainFileProcessingStatus = (
  status: PdgFileProcessingStatus,
): FileProcessingStatus => {
  const mapped = domainFileProcessingStatus[status] as
    | FileProcessingStatus
    | undefined;
  if (mapped) {
    return mapped;
  }
  if (!reportedUnknownFileProcessingStatuses.has(status)) {
    reportedUnknownFileProcessingStatuses.add(status);
    console.warn(
      `Partner Data Gateway: unknown file processing status "${status}"; rendering as ${UNKNOWN_FILE_PROCESSING_STATUS_FALLBACK}`,
    );
  }

  return UNKNOWN_FILE_PROCESSING_STATUS_FALLBACK;
};

const domainFileParsingBatchStatus: Record<
  PdgFileParsingBatchStatus,
  FileParsingBatchStatus
> = {
  [PdgFileParsingBatchStatus.Pending]: FileParsingBatchStatus.Pending,
  [PdgFileParsingBatchStatus.Processing]: FileParsingBatchStatus.Processing,
  [PdgFileParsingBatchStatus.Completed]: FileParsingBatchStatus.Completed,
  [PdgFileParsingBatchStatus.Failed]: FileParsingBatchStatus.Failed,
  [PdgFileParsingBatchStatus.Skipped]: FileParsingBatchStatus.Skipped,
};

const pdgFileParsingBatchStatus: Record<
  FileParsingBatchStatus,
  PdgFileParsingBatchStatus
> = {
  [FileParsingBatchStatus.Pending]: PdgFileParsingBatchStatus.Pending,
  [FileParsingBatchStatus.Processing]: PdgFileParsingBatchStatus.Processing,
  [FileParsingBatchStatus.Completed]: PdgFileParsingBatchStatus.Completed,
  [FileParsingBatchStatus.Failed]: PdgFileParsingBatchStatus.Failed,
  [FileParsingBatchStatus.Skipped]: PdgFileParsingBatchStatus.Skipped,
};

const domainControlAction: Record<PdgControlAction, ControlAction> = {
  [PdgControlAction.Pause]: ControlAction.Pause,
  [PdgControlAction.Resume]: ControlAction.Resume,
  [PdgControlAction.Cancel]: ControlAction.Cancel,
};

const domainConfigurationStatus: Record<
  PdgConfigurationStatus,
  ConfigurationStatus
> = {
  [PdgConfigurationStatus.Draft]: ConfigurationStatus.Draft,
  [PdgConfigurationStatus.Published]: ConfigurationStatus.Published,
  [PdgConfigurationStatus.Deleted]: ConfigurationStatus.Deleted,
};

/** Domain → wire, for mutation variables and list filters. */
export const pdgControlAction: Record<ControlAction, PdgControlAction> = {
  [ControlAction.Pause]: PdgControlAction.Pause,
  [ControlAction.Resume]: PdgControlAction.Resume,
  [ControlAction.Cancel]: PdgControlAction.Cancel,
};

export const pdgConfigurationStatus: Record<
  ConfigurationStatus,
  PdgConfigurationStatus
> = {
  [ConfigurationStatus.Draft]: PdgConfigurationStatus.Draft,
  [ConfigurationStatus.Published]: PdgConfigurationStatus.Published,
  [ConfigurationStatus.Deleted]: PdgConfigurationStatus.Deleted,
};

/**
 * The shape every `Pdg*List` type shares. Codegen emits one concrete list
 * type per resource (`PdgIngestionsList`, `PdgRecordsList`, …) rather than a
 * generic connection, so this structural constraint is what lets a single
 * `toPaginatedResult` serve all of them.
 */
interface PdgList<TItem> {
  items: TItem[];
  pageInfo: PdgPageInfo;
}

/**
 * Codegen stamps every object type with a literal `__typename`, so
 * `PdgIngestionDetail` is *not* assignable to `PdgIngestionSummary` even
 * though it carries all of its fields. Widening the summary mapper's
 * parameter lets `toIngestionDetail` reuse it instead of restating 13
 * fields.
 */
type WithoutTypename<T> = Omit<T, '__typename'>;

export const toPageInfo = (pdg: PdgPageInfo): PageInfo => ({
  hasNextPage: pdg.hasNextPage,
  nextCursor: pdg.nextCursor ?? null,
});

export const toPaginatedResult = <TPdg, TDomain>(
  pdg: PdgList<TPdg>,
  mapItem: (item: TPdg) => TDomain,
): PaginatedResult<TDomain> => ({
  items: pdg.items.map(mapItem),
  pageInfo: toPageInfo(pdg.pageInfo),
});

export const toIngestionSummary = (
  pdg: WithoutTypename<PdgIngestionSummary>,
): IngestionSummary => ({
  eventId: pdg.eventId,
  fileName: pdg.fileName,
  s3Key: pdg.s3Key,
  bucket: pdg.bucket ?? null,
  dataProvider: pdg.dataProvider ?? null,
  fileExtension: pdg.fileExtension ?? null,
  status: toDomainFileProcessingStatus(pdg.status),
  totalBatches: pdg.totalBatches,
  completedBatches: pdg.completedBatches,
  totalRowsParsed: pdg.totalRowsParsed,
  createdAt: pdg.createdAt,
  updatedAt: pdg.updatedAt,
  completedAt: pdg.completedAt ?? null,
});

export const toIngestionDetail = (
  pdg: PdgIngestionDetail,
): IngestionDetail => ({
  ...toIngestionSummary(pdg),
  eTag: pdg.eTag ?? null,
  eventTime: pdg.eventTime ?? null,
  eventType: pdg.eventType ?? null,
  temporalWorkflowId: pdg.temporalWorkflowId,
  liveProgressSource: pdg.liveProgressSource,
  stageHistory: pdg.stageHistory.map(entry => ({
    stage: entry.stage,
    status: entry.status,
    startedAt: entry.startedAt,
    endedAt: entry.endedAt ?? null,
    durationMs: entry.durationMs ?? null,
  })),
  liveProgress: pdg.liveProgress
    ? {
        eventId: pdg.liveProgress.eventId,
        status: pdg.liveProgress.status,
        currentBatchIndex: pdg.liveProgress.currentBatchIndex,
        totalBatches: pdg.liveProgress.totalBatches,
        completedBatches: pdg.liveProgress.completedBatches,
        failedBatches: pdg.liveProgress.failedBatches,
        totalRowsParsed: pdg.liveProgress.totalRowsParsed,
        totalRowsFailed: pdg.liveProgress.totalRowsFailed,
        percentComplete: pdg.liveProgress.percentComplete,
        isPaused: pdg.liveProgress.isPaused,
        isCancelled: pdg.liveProgress.isCancelled,
        validationStatus: pdg.liveProgress.validationStatus ?? null,
      }
    : null,
  childWorkflow: pdg.childWorkflow
    ? {
        workflowId: pdg.childWorkflow.workflowId,
        workflowType: pdg.childWorkflow.workflowType,
        status: pdg.childWorkflow.status,
        startedAt: pdg.childWorkflow.startedAt,
        endedAt: pdg.childWorkflow.endedAt ?? null,
        progress: pdg.childWorkflow.progress
          ? {
              pendingCount: pdg.childWorkflow.progress.pendingCount,
              resolvedCount: pdg.childWorkflow.progress.resolvedCount,
              cachedCount: pdg.childWorkflow.progress.cachedCount ?? null,
              outboxEventsEmitted:
                pdg.childWorkflow.progress.outboxEventsEmitted ?? null,
            }
          : null,
      }
    : null,
  sourceResolution: {
    resolved: pdg.sourceResolution.resolved,
    failed: pdg.sourceResolution.failed,
    quarantined: {
      total: pdg.sourceResolution.quarantined.total,
      noMatch: pdg.sourceResolution.quarantined.noMatch,
      ambiguous: pdg.sourceResolution.quarantined.ambiguous,
    },
  },
  recordMapping: {
    finalized: {
      mapped: pdg.recordMapping.finalized.mapped,
      failed: pdg.recordMapping.finalized.failed,
      dropped: pdg.recordMapping.finalized.dropped,
    },
    live: {
      source: pdg.recordMapping.live.source,
      pending: pdg.recordMapping.live.pending,
      mapped: pdg.recordMapping.live.mapped,
      failed: pdg.recordMapping.live.failed,
      dropped: pdg.recordMapping.live.dropped,
      byFailureReason: pdg.recordMapping.live.byFailureReason,
    },
  },
});

export const toIngestionSummaryStats = (
  pdg: PdgIngestionSummaryStats,
): IngestionSummaryStats => ({
  byStatus: pdg.byStatus,
  totalRowsParsed: pdg.totalRowsParsed,
  window: { hours: pdg.window.hours, since: pdg.window.since },
});

export const toControlResult = (pdg: PdgControlResult): ControlResult => ({
  eventId: pdg.eventId,
  action: domainControlAction[pdg.action],
  status: toDomainFileProcessingStatus(pdg.status),
});

export const toBatchHeatmapCell = (
  pdg: PdgBatchHeatmapCell,
): BatchHeatmapCell => ({
  batchId: pdg.batchId,
  eventId: pdg.eventId,
  status: domainFileParsingBatchStatus[pdg.status],
  byteOffsetStart: pdg.byteOffsetStart,
  byteOffsetEnd: pdg.byteOffsetEnd,
  rowCount: pdg.rowCount,
  errorMessage: pdg.errorMessage ?? null,
  startedAt: pdg.startedAt ?? null,
  createdAt: pdg.createdAt,
  updatedAt: pdg.updatedAt,
});

export const toBatchesPage = (pdg: PdgBatchesList): BatchesPage => ({
  ...toPaginatedResult(pdg, toBatchHeatmapCell),
  totals: {
    total: pdg.totals.total,
    pending: pdg.totals.pending,
    inProgress: pdg.totals.inProgress,
    completed: pdg.totals.completed,
    failed: pdg.totals.failed,
    skipped: pdg.totals.skipped,
  },
});

export const toValidationReport = (
  pdg: PdgValidationReport,
): ValidationReport => ({
  id: pdg.id,
  eventId: pdg.eventId,
  status: pdg.status,
  report: pdg.report,
  createdAt: pdg.createdAt,
});

export const toAssessmentMatch = (
  pdg: PdgAssessmentMatch,
): AssessmentMatch => ({
  id: pdg.id,
  eventId: pdg.eventId,
  configurationId: pdg.configurationId,
  configurationName: pdg.configurationName,
  configurationStatus: pdg.configurationStatus,
  matched: pdg.matched,
  report: pdg.report ?? null,
  createdAt: pdg.createdAt,
});

export const toAssessmentsList = (
  pdg: PdgAssessmentsList,
): AssessmentsList => ({
  items: pdg.items.map(toAssessmentMatch),
});

export const toFileRecordPreview = (
  pdg: PdgFileRecordPreview,
): FileRecordPreview => ({
  id: pdg.id,
  eventId: pdg.eventId,
  batchId: pdg.batchId,
  originalRowIndex: pdg.originalRowIndex,
  rawData: pdg.rawData,
  createdAt: pdg.createdAt,
});

export const toConfiguration = (pdg: PdgConfiguration): Configuration => ({
  id: pdg.id,
  name: pdg.name,
  dataProvider: pdg.dataProvider,
  status: domainConfigurationStatus[pdg.status],
  definition: pdg.definition,
  actions: pdg.actions,
  createdAt: pdg.createdAt,
  updatedAt: pdg.updatedAt,
});

export const toActionsCatalog = (pdg: PdgActionsCatalog): ActionsCatalog => ({
  actionTypes: pdg.actionTypes.map(actionType => ({
    type: actionType.type,
    label: actionType.label,
    description: actionType.description,
    scopes: actionType.scopes,
    fields: actionType.fields,
  })),
});

// ---- domain params → generated input objects ----

export const toListIngestionsInput = (
  params: ListIngestionsParams,
): ListIngestionsInput => ({
  cursor: params.cursor,
  limit: params.limit,
  status: params.status ? pdgFileProcessingStatus[params.status] : undefined,
  dataProvider: params.dataProvider,
  createdAfter: params.createdAfter,
  createdBefore: params.createdBefore,
});

export const toListBatchesInput = (
  params: ListBatchesParams,
): ListBatchesInput => ({
  cursor: params.cursor,
  limit: params.limit,
  status: params.status ? pdgFileParsingBatchStatus[params.status] : undefined,
});

export const toListConfigurationsInput = (
  params: ListConfigurationsParams,
): ListConfigurationsInput => ({
  cursor: params.cursor,
  limit: params.limit,
  status: params.status ? pdgConfigurationStatus[params.status] : undefined,
  dataProvider: params.dataProvider,
  name: params.name,
});
